import React, { useEffect, useState, useCallback } from 'react';
import { toast } from 'react-toastify';
import api from '../utils/axiosConfig';
import '../styles/ScheduleExceptions.css';

export default function ScheduleExceptions() {
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);

  // Form state
  const [isRange, setIsRange] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [exceptionType, setExceptionType] = useState('day_off');
  const [startHour, setStartHour] = useState('09:00');
  const [endHour, setEndHour] = useState('14:00');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  // Conflict dialog state
  const [conflictDialog, setConflictDialog] = useState(null);

  const todayStr = new Date().toISOString().split('T')[0];

  const fetchExceptions = useCallback(async () => {
    try {
      const { data } = await api.get('/backoffice/schedule-exceptions');
      if (data.success) setExceptions(data.exceptions);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchExceptions(); }, [fetchExceptions]);

  const resetForm = () => {
    setStartDate('');
    setEndDate('');
    setExceptionType('day_off');
    setStartHour('09:00');
    setEndHour('14:00');
    setReason('');
    setIsRange(false);
  };

  const submitException = async (cancelBookings) => {
    setSaving(true);
    setConflictDialog(null);
    try {
      const payload = {
        start_date: startDate,
        end_date: isRange ? endDate : startDate,
        exception_type: exceptionType,
        reason,
        cancel_bookings: cancelBookings,
      };
      if (exceptionType === 'custom_hours') {
        payload.start_hour = startHour;
        payload.end_hour = endHour;
      }
      const { data } = await api.post('/backoffice/schedule-exceptions', payload);
      if (data.success) {
        toast.success(data.message);
        if (data.cancelled_bookings) {
          toast.info(`تم إلغاء ${data.cancelled_bookings} حجز`);
        }
        resetForm();
        fetchExceptions();
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!startDate) return toast.error('اختر التاريخ');
    if (isRange && !endDate) return toast.error('اختر تاريخ النهاية');
    if (isRange && endDate < startDate) return toast.error('تاريخ النهاية يجب أن يكون بعد تاريخ البداية');
    if (exceptionType === 'custom_hours' && startHour >= endHour) {
      return toast.error('ساعة البداية يجب أن تكون قبل ساعة النهاية');
    }

    setSaving(true);
    try {
      const { data } = await api.post('/backoffice/schedule-exceptions/check-conflicts', {
        start_date: startDate,
        end_date: isRange ? endDate : startDate,
      });

      if (data.has_conflicts) {
        setSaving(false);
        setConflictDialog({
          bookings: data.conflicting_bookings,
          count: data.conflicting_bookings.length,
        });
        return;
      }

      await submitException(false);
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      const { data } = await api.delete(`/backoffice/schedule-exceptions/${id}`);
      if (data.success) {
        toast.success(data.message);
        setExceptions((prev) => prev.filter((e) => e.id !== id));
      } else {
        toast.error(data.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'حدث خطأ');
    }
  };

  const formatDate = (dateStr) => {
    const d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('ar-EG', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (loading) return null;

  return (
    <div className="schedule-exceptions">
      <h2>استثناءات الجدول</h2>
      <p className="section-desc">أضف أيام إجازة أو ساعات عمل مخصصة لتواريخ محددة</p>

      <form onSubmit={handleSubmit} className="exception-form">
        {/* Range toggle */}
        <div className="range-toggle">
          <button
            type="button"
            className={`toggle-chip ${!isRange ? 'active' : ''}`}
            onClick={() => setIsRange(false)}
          >
            يوم واحد
          </button>
          <button
            type="button"
            className={`toggle-chip ${isRange ? 'active' : ''}`}
            onClick={() => setIsRange(true)}
          >
            فترة (من - إلى)
          </button>
        </div>

        {/* Date inputs */}
        <div className="form-row">
          <div className="form-group">
            <label>{isRange ? 'من تاريخ' : 'التاريخ'}</label>
            <input
              type="date"
              value={startDate}
              min={todayStr}
              onChange={(e) => setStartDate(e.target.value)}
              dir="ltr"
            />
          </div>
          {isRange && (
            <div className="form-group">
              <label>إلى تاريخ</label>
              <input
                type="date"
                value={endDate}
                min={startDate || todayStr}
                onChange={(e) => setEndDate(e.target.value)}
                dir="ltr"
              />
            </div>
          )}
        </div>

        {/* Exception type */}
        <div className="type-toggle">
          <button
            type="button"
            className={`toggle-chip ${exceptionType === 'day_off' ? 'active' : ''}`}
            onClick={() => setExceptionType('day_off')}
          >
            يوم إجازة
          </button>
          <button
            type="button"
            className={`toggle-chip ${exceptionType === 'custom_hours' ? 'active' : ''}`}
            onClick={() => setExceptionType('custom_hours')}
          >
            ساعات مخصصة
          </button>
        </div>

        {/* Custom hours */}
        {exceptionType === 'custom_hours' && (
          <div className="form-row">
            <div className="form-group">
              <label>بداية الدوام</label>
              <input
                type="time"
                value={startHour}
                onChange={(e) => setStartHour(e.target.value)}
                dir="ltr"
              />
            </div>
            <div className="form-group">
              <label>نهاية الدوام</label>
              <input
                type="time"
                value={endHour}
                onChange={(e) => setEndHour(e.target.value)}
                dir="ltr"
              />
            </div>
          </div>
        )}

        {/* Reason */}
        <div className="form-group">
          <label>السبب (اختياري)</label>
          <input
            type="text"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="مثال: إجازة، يوم قصير..."
          />
        </div>

        <button type="submit" className="save-btn" disabled={saving}>
          {saving ? 'جاري الحفظ...' : 'إضافة استثناء'}
        </button>
      </form>

      {/* Existing exceptions list */}
      {exceptions.length > 0 && (
        <div className="exceptions-list">
          <h3>الاستثناءات الحالية</h3>
          {exceptions.map((exc) => (
            <div key={exc.id} className={`exception-card ${exc.exception_type}`}>
              <div className="exception-info">
                <span className={`exception-badge ${exc.exception_type}`}>
                  {exc.exception_type === 'day_off' ? 'إجازة' : 'ساعات مخصصة'}
                </span>
                <span className="exception-dates">
                  {formatDate(exc.start_date)}
                  {exc.start_date !== exc.end_date && (
                    <> ← {formatDate(exc.end_date)}</>
                  )}
                </span>
                {exc.exception_type === 'custom_hours' && (
                  <span className="exception-hours">{exc.start_hour} - {exc.end_hour}</span>
                )}
                {exc.reason && <span className="exception-reason">{exc.reason}</span>}
              </div>
              <button className="delete-btn" onClick={() => handleDelete(exc.id)} title="حذف">✕</button>
            </div>
          ))}
        </div>
      )}

      {/* Conflict dialog */}
      {conflictDialog && (
        <div className="conflict-overlay" onClick={() => setConflictDialog(null)}>
          <div className="conflict-dialog" onClick={(e) => e.stopPropagation()}>
            <h3>تنبيه - حجوزات موجودة</h3>
            <p>يوجد <strong>{conflictDialog.count}</strong> حجز في هذه الفترة:</p>
            <div className="conflict-bookings">
              {conflictDialog.bookings.slice(0, 5).map((b) => (
                <div key={b.id} className="conflict-booking-item">
                  <span>{b.full_name || b.phone}</span>
                  <span>{b.date} - {b.time}</span>
                </div>
              ))}
              {conflictDialog.count > 5 && (
                <p className="more-bookings">و {conflictDialog.count - 5} حجوزات أخرى...</p>
              )}
            </div>
            <div className="conflict-actions">
              <button className="action-btn cancel-action" onClick={() => submitException(true)}>
                إلغاء الحجوزات وإضافة الاستثناء
              </button>
              <button className="action-btn keep-action" onClick={() => submitException(false)}>
                الاحتفاظ بالحجوزات وإضافة الاستثناء
              </button>
              <button className="action-btn dismiss-action" onClick={() => setConflictDialog(null)}>
                تراجع
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
